import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaTruck, FaCalendarAlt, FaMapMarkerAlt, FaWeightHanging, 
  FaArrowRight, FaEye, FaUserTie, FaCheckCircle, 
  FaClock, FaExclamationTriangle, FaBox,
  FaClipboardList, FaPlus, FaSearch, FaFilter,
  FaEdit, FaTrash, FaInfoCircle, FaChartLine,
  FaDollarSign, FaUsers, FaFileAlt, FaDownload,
  FaPrint, FaShare, FaUpload, FaImage, FaFilePdf,
  FaCalendarCheck, FaHourglassHalf, FaFlagCheckered,
  FaHistory
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';

function SubbieActiveJobs({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [user, setUser] = useState({});
  const [subbieId, setSubbieId] = useState(null);
  const [jobProgress, setJobProgress] = useState({});

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    getSubbieId(userData);
  }, []);

  const getSubbieId = async (userData) => {
    try {
      let savedId = localStorage.getItem('subbieId');
      if (savedId) {
        setSubbieId(savedId);
        fetchJobs(savedId);
        return;
      }

      const response = await fetch(`${API_URL}/subcontractors?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.subcontractors && data.subcontractors.length > 0) {
        const id = data.subcontractors[0].id;
        setSubbieId(id);
        localStorage.setItem('subbieId', id);
        fetchJobs(id);
      } else {
        setLoading(false);
        toast.error('No subcontractor profile found');
      }
    } catch (err) {
      console.error('Error fetching subbie ID:', err);
      toast.error('Failed to load profile');
      setLoading(false);
    }
  };

  const fetchJobs = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/subbie/all-requirements/${id}`);
      const data = await response.json();
      
      if (data.success) {
        const requirements = data.requirements || [];
        
        const activeJobs = requirements.filter(r => 
          r.status !== 'completed' && 
          r.status !== 'closed' &&
          (r.status === 'approved' || 
           r.status === 'accepted' || 
           r.status === 'assigned' ||
           r.status === 'in_progress')
        );
        
        setJobs(activeJobs);
        
        const allocData = {};
        const progressData = {};
        
        for (const job of activeJobs) {
          if (job.job_id) {
            try {
              const allocRes = await fetch(`${API_URL}/fleet/fleet-allocations?job_id=${job.job_id}`);
              const allocResult = await allocRes.json();
              if (allocResult.success) {
                const allAllocations = allocResult.allocations || [];
                
                // ✅ FIX: Separate active from completed
                const activeAllocations = allAllocations.filter(a => 
                  a.status !== 'delivered' && 
                  a.status !== 'completed' &&
                  a.deleted_at === null
                );
                
                const completedAllocations = allAllocations.filter(a => 
                  (a.status === 'delivered' || a.status === 'completed') &&
                  a.deleted_at === null
                );
                
                allocData[job.job_id] = {
                  active: activeAllocations,
                  completed: completedAllocations,
                  all: allAllocations
                };
                
                // ✅ FIX: Calculate delivered MT correctly
                const totalMT = job.jobs?.total_quantity_mt || 0;
                let deliveredMT = 0;
                
                allAllocations.forEach(unit => {
                  // ✅ Check BOTH delivered AND completed
                  if (unit.status === 'delivered' || unit.status === 'completed') {
                    // ✅ Use weighbridge_weight_mt (this is where the weight is stored)
                    const weight = unit.weighbridge_weight_mt || unit.pod_weight_mt || 0;
                    deliveredMT += parseFloat(weight) || 0;
                  }
                });
                
                progressData[job.job_id] = {
                  total: totalMT,
                  delivered: deliveredMT,
                  percentage: totalMT > 0 ? Math.round((deliveredMT / totalMT) * 100) : 0,
                  remaining: Math.max(0, totalMT - deliveredMT),
                  deliveredCount: completedAllocations.length,
                  activeCount: activeAllocations.length,
                  isComplete: deliveredMT >= totalMT && totalMT > 0
                };
              }
            } catch (err) {
              console.error('Error fetching allocations for job:', job.job_id, err);
              allocData[job.job_id] = { active: [], completed: [], all: [] };
              progressData[job.job_id] = { 
                total: 0, 
                delivered: 0, 
                percentage: 0, 
                remaining: 0, 
                deliveredCount: 0, 
                activeCount: 0,
                isComplete: false
              };
            }
          }
        }
        
        setAllocations(allocData);
        setJobProgress(progressData);
      }
    } catch (err) {
      console.error('Error fetching active jobs:', err);
      setError(err.message || 'Failed to load active jobs');
      toast.error('Failed to load active jobs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'quoting': 'bg-blue-100 text-blue-700',
      'approved': 'bg-green-100 text-green-700',
      'accepted': 'bg-green-100 text-green-700',
      'assigned': 'bg-purple-100 text-purple-700',
      'in_progress': 'bg-indigo-100 text-indigo-700',
      'completed': 'bg-gray-100 text-gray-700',
      'closed': 'bg-gray-100 text-gray-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '⏳ Pending',
      'quoting': '📝 Quoting',
      'approved': '✅ Approved',
      'accepted': '✅ Accepted',
      'assigned': '🚛 Assigned',
      'in_progress': '🔄 In Progress',
      'completed': '✅ Completed',
      'closed': '🔒 Closed'
    };
    return labels[status] || status;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'low': 'bg-gray-100 text-gray-600',
      'normal': 'bg-blue-100 text-blue-600',
      'high': 'bg-orange-100 text-orange-600',
      'urgent': 'bg-red-100 text-red-600'
    };
    return `px-2 py-0.5 rounded-full text-xs font-semibold ${styles[priority?.toLowerCase()] || 'bg-gray-100 text-gray-600'}`;
  };

  const getTypeLabel = (type) => {
    const labels = { minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal', container: '📦 Container', general: '📋 General' };
    return labels[type] || type;
  };

  const handleViewScheduler = (jobId) => {
    console.log('🔍 Navigating to scheduler for job:', jobId);
    navigate(`/subbie-scheduler/${jobId}`);
  };

  const handleViewJob = (job) => {
    toast.info('Job details view coming soon');
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="activejobs" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading active jobs...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error) {
    return (
      <SubbieLayout activeTab="activejobs" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Jobs</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchJobs(subbieId)} 
            className="mt-4 px-4 py-2 rounded-lg text-white" 
            style={{ backgroundColor: '#00A86B' }}
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  return (
    <SubbieLayout activeTab="activejobs" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">📋 Active Jobs</h1>
          <p className="text-sm text-gray-500">
            Jobs ready for fleet allocation or in progress
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <span className="text-sm text-gray-500">
            {jobs.length} active {jobs.length === 1 ? 'job' : 'jobs'}
          </span>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-semibold text-navy mb-2">No Active Jobs</h3>
          <p className="text-sm text-gray-500">
            You don't have any active jobs right now. Check your new jobs for opportunities.
          </p>
          <Link to="/subbie-new-jobs" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            <FaClipboardList className="inline mr-2" /> View New Jobs
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => {
            const jobAllocations = allocations[job.job_id] || { active: [], completed: [], all: [] };
            const activeTrucks = jobAllocations.active || [];
            const completedTrucks = jobAllocations.completed || [];
            const totalActive = activeTrucks.length;
            const totalCompleted = completedTrucks.length;
            const requiredTrucks = job.trucks_required || 2;
            const progress = jobProgress[job.job_id] || { 
              total: 0, 
              delivered: 0, 
              percentage: 0, 
              remaining: 0, 
              deliveredCount: 0, 
              activeCount: 0,
              isComplete: false
            };
            
            const remainingMT = progress.remaining || 0;
            const isComplete = progress.isComplete || progress.percentage >= 100;
            
            return (
              <div key={job.id} className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  {/* Left - Job Info */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-navy">{job.jobs?.job_number || 'Unknown Job'}</h3>
                      <span className={getStatusBadge(job.status)}>{getStatusLabel(job.status)}</span>
                      {job.jobs?.quote_type && job.jobs.quote_type !== 'minerals' && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-700">
                          {getTypeLabel(job.jobs.quote_type)}
                        </span>
                      )}
                      {job.priority && (
                        <span className={getPriorityBadge(job.priority)}>
                          {job.priority.toUpperCase()}
                        </span>
                      )}
                      {totalActive > 0 && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          {totalActive} active truck{totalActive !== 1 ? 's' : ''}
                        </span>
                      )}
                      {totalCompleted > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          ✅ {totalCompleted} completed
                        </span>
                      )}
                      {isComplete && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          🏁 All delivered!
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">
                      <FaTruck className="inline mr-1 text-green-500" /> 
                      Via One Purpose Logistics · {job.trucks_required || 1} truck{(job.trucks_required || 1) > 1 ? 's' : ''} required
                    </p>
                    {job.jobs?.quote_type === 'abnormal' && (job.jobs?.job_description || job.jobs?.commodity) && (
                      <p className="text-xs text-gray-500 mb-2 line-clamp-2 whitespace-pre-line">
                        {job.jobs.job_description || job.jobs.commodity}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-3">
                      {job.jobs?.total_quantity_mt > 0 && (
                        <span className="flex items-center gap-1">
                          <FaWeightHanging size={14} className="text-green-500" /> 
                          {job.jobs.total_quantity_mt} MT
                        </span>
                      )}
                      {job.jobs?.quote_type === 'container' && job.jobs?.container_size && (
                        <span className="flex items-center gap-1">
                          📦 {job.jobs.container_count || ''} × {job.jobs.container_size}{job.jobs.container_type ? ` ${job.jobs.container_type}` : ''}{job.jobs.is_empty ? ' (empty)' : ' (loaded)'}
                        </span>
                      )}
                      {job.jobs?.combined_weight_t > 0 && (
                        <span className="flex items-center gap-1">
                          ⚖️ {job.jobs.combined_weight_t} t combined
                        </span>
                      )}
                      {job.jobs?.cross_border && (
                        <span className="flex items-center gap-1">
                          🌍 Cross-border
                        </span>
                      )}
                      {progress.total > 0 && (
                        <span className="flex items-center gap-1 text-green-600 font-medium">
                          <FaCheckCircle size={14} /> {progress.delivered.toFixed(1)} MT delivered
                        </span>
                      )}
                      {remainingMT > 0 && (
                        <span className="flex items-center gap-1 text-orange-500">
                          <FaClock size={14} /> {remainingMT.toFixed(1)} MT remaining
                        </span>
                      )}
                      {remainingMT === 0 && progress.total > 0 && (
                        <span className="flex items-center gap-1 text-green-600">
                          🎉 All {progress.delivered.toFixed(1)} MT delivered!
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaMapMarkerAlt size={14} className="text-blue-500" /> 
                        {job.jobs?.origin || 'Unknown'} → {job.jobs?.destination || 'Unknown'}
                      </span>
                      {job.jobs?.subbie_target_price > 0 && (
                        <span className="flex items-center gap-1 font-medium text-green-600">
                          <FaDollarSign size={14} /> R{Number(job.jobs.subbie_target_price).toFixed(2)}/MT
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} className="text-gray-400" /> 
                        {job.jobs?.start_date || 'TBD'}
                      </span>
                    </div>
                  </div>

                  {/* Right - Actions */}
                  <div className="flex flex-wrap gap-2">
                    {!isComplete ? (
                      <button
                        onClick={() => handleViewScheduler(job.job_id)}
                        style={{
                          backgroundColor: '#00A86B',
                          color: '#FFFFFF',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '2px solid #008f5a',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#008f5a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                      >
                        <FaTruck size={14} style={{ color: '#FFFFFF' }} /> View Scheduler
                      </button>
                    ) : (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 16px',
                          backgroundColor: '#d1fae5',
                          color: '#047857',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: '2px solid #a7f3d0',
                          fontSize: '14px'
                        }}
                      >
                        <FaCheckCircle size={14} /> ✅ Complete
                      </span>
                    )}
                    <button
                      onClick={() => handleViewJob(job)}
                      style={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontWeight: 'bold',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '2px solid #d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    >
                      <FaEye size={14} /> View
                    </button>
                  </div>
                </div>

                {/* MT Progress Bar - PERMANENT DELIVERED COUNT */}
                {progress.total > 0 && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-gray-600">Delivery Progress</span>
                      <span className="text-xs font-bold text-green-600">{progress.percentage}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min(progress.percentage, 100)}%`, 
                          backgroundColor: progress.percentage >= 100 ? '#047857' : '#00A86B' 
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{progress.delivered.toFixed(1)} MT delivered</span>
                      <span>{progress.total.toFixed(1)} MT total</span>
                    </div>
                    {progress.remaining > 0 && (
                      <div className="text-xs text-orange-500 mt-1">
                        {progress.remaining.toFixed(1)} MT remaining to deliver
                      </div>
                    )}
                    {progress.remaining === 0 && progress.total > 0 && (
                      <div className="text-xs text-green-600 mt-1 font-medium">
                        🎉 All {progress.total.toFixed(1)} MT delivered!
                      </div>
                    )}
                  </div>
                )}

                {/* TWO SECTIONS: Active Trucks + Completed Trucks */}
                <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E8ECEF' }}>
                  {/* Active Trucks Section */}
                  {activeTrucks.length > 0 && (
                    <div className="mb-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-blue-600 mb-1">
                        <FaTruck size={12} /> Active Trucks ({activeTrucks.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {activeTrucks.slice(0, 5).map((unit, idx) => {
                          const statusColors = {
                            'pending': 'bg-gray-200',
                            'on_route_to_loading': 'bg-indigo-200',
                            'onsite': 'bg-blue-200',
                            'loading': 'bg-yellow-200',
                            'en_route': 'bg-purple-200',
                            'at_destination': 'bg-orange-200',
                            'delivered': 'bg-green-200',
                            'completed': 'bg-green-300'
                          };
                          return (
                            <span 
                              key={idx} 
                              className={`px-2 py-0.5 rounded text-xs ${statusColors[unit.status] || 'bg-gray-200'} text-gray-700`}
                              title={`${unit.truck?.registration_number || 'Unit'}: ${unit.status}`}
                            >
                              {unit.truck?.registration_number || `U${idx + 1}`}
                              <span className="ml-1 text-gray-400 text-[10px]">
                                ({unit.status.replace('_', ' ')})
                              </span>
                            </span>
                          );
                        })}
                        {activeTrucks.length > 5 && (
                          <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                            +{activeTrucks.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Completed Trucks Section */}
                  {completedTrucks.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs font-medium text-green-600 mb-1">
                        <FaHistory size={12} /> Completed Trucks ({completedTrucks.length})
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {completedTrucks.map((unit, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 flex items-center gap-1"
                            title={`${unit.truck?.registration_number || 'Unit'}: Completed`}
                          >
                            <FaCheckCircle size={10} />
                            {unit.truck?.registration_number || `U${idx + 1}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Summary Row */}
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <FaUsers className="text-blue-500" />
                    {activeTrucks.filter(a => a.driver_id).length} drivers assigned
                  </span>
                  {totalActive >= requiredTrucks && totalActive > 0 && (
                    <span className="text-green-600 font-medium">✅ All trucks allocated!</span>
                  )}
                  {!isComplete && remainingMT > 0 && (
                    <span className="text-orange-600 font-medium">
                      ⚠️ {Math.ceil(remainingMT / (job.jobs?.load_size_per_truck || 30))} more truck{Math.ceil(remainingMT / (job.jobs?.load_size_per_truck || 30)) !== 1 ? 's' : ''} needed
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SubbieLayout>
  );
}

export default SubbieActiveJobs;
